import * as React from 'react';
import { AppUser } from 'Src/views/App/App';
import { SchoolEvent } from 'Src/models/event';
import {
  Tournament,
  TournamentCompetitionDefinition,
  TournamentCompetitionRuntime,
  TournamentCustomGroup
} from 'Src/models/tournament';
import { DatePicker } from 'Src/components/DatePicker/DatePicker';
import { TimePicker } from 'Src/components/TimePicker/TimePicker';
import { SchedulePosition } from './TournamentScheduleGrid';
import { getAllTournamentEvents, updateTournamentEventsBulk } from 'Src/helpers/service/adminSU/tournamentEventsTable';
import { initialStructureLayout } from './TournamentStructureGroupsBoard';
import { SchoolMark } from './TournamentStructureWorkspace';

interface Props {
  user: AppUser;
  tournament: Tournament;
  runtime?: TournamentCompetitionRuntime;
  group: TournamentCustomGroup;
  definition: TournamentCompetitionDefinition;
  defaultDuration: number;
  onSaving: (saving: boolean) => void;
  onScheduleChange: (matchId: string, position: SchedulePosition, published: boolean) => void;
}

const dateRank = (value?: string) => {
  const timestamp = Date.parse(value || '');
  return Number.isFinite(timestamp) ? timestamp : Infinity;
};

export const TournamentInitialGroupFixtures: React.FC<Props> = ({ user, tournament, runtime, group, definition, defaultDuration, onSaving, onScheduleChange }) => {
  const [editing, setEditing] = React.useState<{ id: string; start: Date | null; pitchId: string }>();
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState('');
  const [events, setEvents] = React.useState<SchoolEvent[]>();
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [retry, setRetry] = React.useState(0);
  const initialLayout = initialStructureLayout(tournament, runtime);
  const publishedLayout = (runtime?.structure || tournament.competitionStructure?.published)?.layout || initialLayout;
  const draftLayout = definition.layout || initialLayout;
  const stageGroups = (layout: typeof initialLayout) => layout.groups.filter(item => item.stageId === group.stageId);
  const stageGroupIds = new Set(
    [...stageGroups(publishedLayout), ...stageGroups(draftLayout), group].map(item => item.id)
  );
  const stageGroupKey = [...stageGroupIds].sort().join(',');

  React.useEffect(() => {
    let cancelled = false;
    setEvents(undefined);
    setLoadFailed(false);
    getAllTournamentEvents(user, tournament.id, { tournamentGroupId: { $in: stageGroupKey.split(',') } })
      .then((value: SchoolEvent[]) => {
        if (!cancelled) setEvents(value);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user.activeSchoolId, user.roleSessionKey, tournament.id, stageGroupKey, retry]);

  const teamGroups = (layout: typeof initialLayout) => {
    const slots = new Map<string, string>();
    stageGroups(layout).forEach(item => {
      const teamIds = layout.assignments.find(assignment => assignment.groupId === item.id)?.teamIds || [];
      teamIds.forEach(id => slots.set(id, item.id));
    });
    return slots;
  };
  const liveGroups = teamGroups(publishedLayout);
  const draftGroups = teamGroups(draftLayout);
  const teamSide = (
    id: string,
    groups: ReturnType<typeof teamGroups>,
    fallback?: SchoolEvent['embeddedTeams'][number]
  ) => {
    const team = runtime?.teams.find(item => item.id === id);
    const school = tournament.schools?.find(item => item.id === (team?.schoolId || fallback?.schoolId));
    return {
      groupId: groups.get(id),
      schoolName: school?.name || 'School to be confirmed',
      teamName: team?.name || fallback?.name,
      schoolPic: school?.pic
    };
  };
  const linkedMatchIds = new Set((events || []).map(event => event.tournamentStructureMatchId).filter(Boolean));
  const eventIds = new Set((events || []).map(event => event.id));
  const runtimeEvents = new Map(runtime?.matches.map(match => [match.matchId, match.eventId]));
  const matchByEvent = new Map<string, string>();
  definition.matches.forEach(match => {
    const eventId = runtimeEvents.get(match.id || '') || match.eventId;
    if (eventId && match.id) matchByEvent.set(eventId, match.id);
  });
  const fixtures = [
    ...(events || []).map(event => ({
      id: `event-${event.id}`,
      event,
      matchId: event.tournamentStructureMatchId || matchByEvent.get(event.id),
      endTime: event.endTime,
      groupId: event.tournamentGroupId,
      startTime: event.startTime,
      pitchId: event.pitchId,
      sides: (event.embeddedTeams || []).map(team => teamSide(team.cloneOf || team._id, liveGroups, team))
    })),
    ...definition.matches
      .filter(
        match =>
          !match.removed &&
          (!match.round || match.round === 'GROUP') &&
          stageGroupIds.has(match.groupId) &&
          !linkedMatchIds.has(match.id) &&
          !eventIds.has(match.eventId || runtimeEvents.get(match.id || '') || '')
      )
      .map(match => ({
        id: `planned-${match.id}`,
        event: undefined as SchoolEvent | undefined,
        matchId: match.id,
        endTime: match.endTime,
        groupId: match.groupId,
        startTime: match.startTime,
        pitchId: match.pitchId,
        sides: [match.homeSource, match.awaySource].map(source => teamSide(source.teamId || '', draftGroups))
      }))
  ]
    .filter(fixture => fixture.groupId === group.id || fixture.sides.some(side => side.groupId === group.id))
    .sort((a, b) => dateRank(a.startTime) - dateRank(b.startTime) || 0);
  const showDate = new Date(tournament.startTime).toDateString() !== new Date(tournament.endTime).toDateString();

  const saveSchedule = async (fixture: typeof fixtures[number]) => {
    if (!editing?.start || !Number.isFinite(+editing.start)) {
      setSaveError('Choose a start date and time.');
      return;
    }
    if (fixture.event && +editing.start <= Date.now()) {
      setSaveError('Choose a start time in the future.');
      return;
    }
    const duration = dateRank(fixture.endTime) - dateRank(fixture.startTime);
    const position: SchedulePosition = {
      startTime: editing.start.toISOString(),
      endTime: new Date(+editing.start + (duration > 0 && Number.isFinite(duration) ? duration : defaultDuration * 60000)).toISOString(),
      pitchId: editing.pitchId || null
    };
    setSaving(true);
    onSaving(true);
    setSaveError('');
    try {
      if (fixture.event) {
        await updateTournamentEventsBulk(user, tournament.id, [{ eventId: fixture.event.id, ...position }], true);
        setEvents(current => current?.map(event => event.id === fixture.event.id
          ? { ...event, ...position, pitchId: position.pitchId || undefined } : event));
      }
      if (fixture.matchId) onScheduleChange(fixture.matchId, position, Boolean(fixture.event));
      setEditing(undefined);
    } catch (error) {
      setSaveError(error?.response?.data?.details?.text || error?.response?.data?.message || 'The fixture could not be updated.');
    } finally {
      setSaving(false);
      onSaving(false);
    }
  };

  return (
    <div className="tcsBracket">
      <header>
        <div>
          <span className="tswEyebrow">Group stage</span>
          <h2>{group.name}</h2>
          <p>Fixtures by start time.</p>
        </div>
      </header>
      {loadFailed ? (
        <p role="alert">
          Existing fixtures could not be loaded.{' '}
          <button type="button" onClick={() => setRetry(value => value + 1)}>
            Retry
          </button>
        </p>
      ) : (
        !events && <p role="status">Loading fixtures…</p>
      )}
      {fixtures.length > 0 ? (
        <div className="tswFixturesWrap">
          <table className="tswFixtures" aria-label={`${group.name} fixtures`}>
            <thead>
              <tr>
                <th>Start time</th>
                <th>Schools</th>
                <th>Pitch</th>
                <th><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {fixtures.map(fixture => (
                <React.Fragment key={fixture.id}><tr>
                  <td>
                    {Number.isFinite(dateRank(fixture.startTime))
                      ? new Date(fixture.startTime!).toLocaleString('en-GB', {
                          ...(showDate ? { day: 'numeric', month: 'short' } : {}),
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      : 'Not set'}
                  </td>
                  <td>
                    <div className="tswFixtureSides">
                      {fixture.sides.map((side, index) => (
                        <div key={index}>
                          {side.schoolName && <SchoolMark name={side.schoolName} pic={side.schoolPic} />}
                          <span>{[side.schoolName, side.teamName !== side.schoolName && side.teamName].filter(Boolean).join(' · ')}</span>
                        </div>
                      ))}
                    </div>
                  </td>
                  <td>{tournament.pitches?.find(pitch => pitch.id === fixture.pitchId)?.name || 'Not set'}</td>
                  <td>{(!fixture.event || (!['FINISHED', 'CANCELED'].includes(fixture.event.status) && dateRank(fixture.startTime) > Date.now())) && (fixture.event || fixture.matchId) ? (
                    <button type="button" className="tswScheduleAction" disabled={saving} onClick={() => {
                      setSaveError('');
                      setEditing({ id: fixture.id, start: new Date(fixture.startTime || tournament.startTime), pitchId: fixture.pitchId || '' });
                    }}>Edit schedule</button>
                  ) : null}</td>
                </tr>
                {editing?.id === fixture.id && <tr><td colSpan={4}>
                  <form className="tswScheduleControls" onSubmit={event => { event.preventDefault(); void saveSchedule(fixture); }}>
                    <DatePicker label="Fixture date" value={editing.start} required clearable={false} size="sm" disabled={saving} onChange={value => {
                      if (!(value instanceof Date)) return;
                      const start = new Date(value);
                      if (editing.start) start.setHours(editing.start.getHours(), editing.start.getMinutes());
                      setEditing({ ...editing, start });
                    }} />
                    <TimePicker label="Fixture start time" value={editing.start} required minuteStep={1} clearable={false} size="sm" disabled={saving} onChange={value => {
                      if (!value) return;
                      const start = new Date(editing.start || tournament.startTime);
                      start.setHours(value.getHours(), value.getMinutes(), 0, 0);
                      setEditing({ ...editing, start });
                    }} />
                    <label className="tswSchedulePitch">Pitch<select disabled={saving} value={editing.pitchId} onChange={event => setEditing({ ...editing, pitchId: event.target.value })}>
                      <option value="">Not set</option>
                      {(tournament.pitches || []).map(pitch => <option key={pitch.id} value={pitch.id}>{pitch.name}</option>)}
                    </select></label>
                    <button type="button" className="tswScheduleAction" disabled={saving} onClick={() => setEditing(undefined)}>Cancel</button>
                    <button type="button" className="tswScheduleAction is-primary" disabled={saving} onClick={event => {
                      if (event.currentTarget.form?.reportValidity()) void saveSchedule(fixture);
                    }}>
                      {saving ? 'Saving…' : fixture.event ? 'Save fixture' : 'Apply to draft'}
                    </button>
                  </form>
                  {saveError && <p role="alert">{saveError}</p>}
                </td></tr>}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        events && <div className="tcsBracket__empty">No fixtures scheduled for this group.</div>
      )}
    </div>
  );
};
